import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Briefcase, Target, FileText, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { fetchPrograms } from '../services/api';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = window.location.origin;

const PreQualificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || null;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [VAE_PROGRAMS, setPrograms] = useState([]);
  
  const [formData, setFormData] = useState({
    // Informations personnelles
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Expérience professionnelle
    currentPosition: '',
    employer: '',
    experienceYears: '',
    mainTasks: '',
    
    // Projet VAE
    targetDiploma: '',
    motivations: '',
    availability: '',
    
    // Autres
    hasCV: false,
    questions: ''
  });

  // Check if user already filled the form
  useEffect(() => {
    fetchPrograms().then(setPrograms).catch(console.error);
    const savedData = localStorage.getItem('preQualificationData');
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.firstName && formData.lastName && formData.email && formData.phone;
      case 2:
        return formData.currentPosition && formData.employer && formData.experienceYears && formData.mainTasks;
      case 3:
        return formData.targetDiploma && formData.motivations && formData.availability;
      case 4:
        return true; // Optional step
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    } else {
      toast.error('Veuillez remplir tous les champs obligatoires');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit to backend API (sends notification email)
      const response = await axios.post(`${BACKEND_URL}/api/pre-qualification/submit`, formData);
      
      // Save to localStorage for frontend state
      localStorage.setItem('preQualificationData', JSON.stringify(formData));
      localStorage.setItem('preQualificationCompleted', 'true');
      localStorage.setItem('preQualificationDate', new Date().toISOString());

      if (response.data.email_sent) {
        toast.success('Votre profil a été enregistré et nous avons été notifiés de votre demande !');
      } else {
        toast.success('Votre profil a été enregistré avec succès !');
      }
      
      setIsSubmitting(false);

      // Redirect to booking if came from there, otherwise to home
      if (redirectTo === 'booking') {
        navigate('/booking');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error submitting pre-qualification:', error);
      
      // Still save to localStorage as fallback
      localStorage.setItem('preQualificationData', JSON.stringify(formData));
      localStorage.setItem('preQualificationCompleted', 'true');
      localStorage.setItem('preQualificationDate', new Date().toISOString());
      
      toast.success('Votre profil a été enregistré !');
      setIsSubmitting(false);
      
      if (redirectTo === 'booking') {
        navigate('/booking');
      } else {
        navigate('/');
      }
    }
  };

  const steps = [
    { num: 1, title: 'Informations personnelles', icon: User },
    { num: 2, title: 'Expérience professionnelle', icon: Briefcase },
    { num: 3, title: 'Projet VAE', icon: Target },
    { num: 4, title: 'Informations complémentaires', icon: FileText }
  ];

  const inputStyle = {
    width: '100%',
    padding: '0.875rem 1rem',
    borderRadius: '0.5rem',
    border: '1px solid var(--border-light)',
    fontSize: '0.875rem',
    background: 'white',
    transition: 'border-color 0.2s ease'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    fontSize: '0.875rem'
  };

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh', background: 'var(--bg-page)' }}>
      {/* Hero */}
      <section style={{ 
        background: 'var(--gradient-hero-warm)',
        padding: '3rem 1.2rem 2rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: '#E75480',
            color: 'white',
            borderRadius: '2rem',
            padding: '0.25rem 0.75rem',
            marginBottom: '1rem'
          }}>
            <span className="caption font-mono" style={{ textTransform: 'uppercase', fontWeight: 600 }}>
              Étape importante
            </span>
          </div>
          <h1 className="heading-hero" style={{ marginBottom: '1rem' }}>
            Formulaire de pré-qualification
          </h1>
          <p className="body-large" style={{ maxWidth: '600px', margin: '0 auto' }}>
            Ces informations nous permettront de personnaliser la rédaction de votre Livret 1 
            en fonction de votre expérience, votre lieu de travail et le diplôme visé.
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="container" style={{ maxWidth: '800px', padding: '2rem 1.2rem 0' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          position: 'relative',
          marginBottom: '3rem'
        }}>
          {/* Progress Line */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '10%',
            right: '10%',
            height: '2px',
            background: 'var(--border-light)',
            zIndex: 0
          }} />
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '10%',
            width: `${((currentStep - 1) / 3) * 80}%`,
            height: '2px',
            background: '#E75480',
            zIndex: 1,
            transition: 'width 0.3s ease'
          }} />

          {steps.map((step) => {
            const Icon = step.icon;
            const isActive = currentStep === step.num;
            const isCompleted = currentStep > step.num;

            return (
              <div key={step.num} style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                position: 'relative',
                zIndex: 2
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isCompleted ? '#4CAF50' : isActive ? '#E75480' : 'white',
                  border: isActive || isCompleted ? 'none' : '2px solid var(--border-light)',
                  color: isActive || isCompleted ? 'white' : 'var(--text-muted)',
                  transition: 'all 0.3s ease'
                }}>
                  {isCompleted ? <CheckCircle size={20} /> : <Icon size={20} />}
                </div>
                <span className="caption" style={{ 
                  marginTop: '0.5rem',
                  textAlign: 'center',
                  color: isActive ? '#E75480' : isCompleted ? '#4CAF50' : 'var(--text-muted)',
                  fontWeight: isActive ? 600 : 400,
                  maxWidth: '100px'
                }}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Form */}
      <div className="container" style={{ maxWidth: '700px', padding: '0 1.2rem 4rem' }}>
        <form onSubmit={handleSubmit}>
          <div className="card" style={{ padding: '2rem' }}>
            
            {/* Step 1: Informations personnelles */}
            {currentStep === 1 && (
              <div>
                <h2 className="heading-2" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <User size={24} style={{ color: '#E75480' }} />
                  Informations personnelles
                </h2>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Prénom *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Votre prénom"
                      style={inputStyle}
                      required
                      data-testid="input-firstName"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Nom *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Votre nom"
                      style={inputStyle}
                      required
                      data-testid="input-lastName"
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="votre@email.fr"
                    style={inputStyle}
                    required
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label style={labelStyle}>Téléphone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="06 12 34 56 78"
                    style={inputStyle}
                    required
                    data-testid="input-phone"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Expérience professionnelle */}
            {currentStep === 2 && (
              <div>
                <h2 className="heading-2" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Briefcase size={24} style={{ color: '#E75480' }} />
                  Expérience professionnelle
                </h2>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Poste actuel ou dernier poste occupé *</label>
                  <input
                    type="text"
                    name="currentPosition"
                    value={formData.currentPosition}
                    onChange={handleInputChange}
                    placeholder="Ex: Auxiliaire de puériculture, ATSEM, Assistante maternelle..."
                    style={inputStyle}
                    required
                    data-testid="input-currentPosition"
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Nom de l'employeur / Structure *</label>
                  <input
                    type="text"
                    name="employer"
                    value={formData.employer}
                    onChange={handleInputChange}
                    placeholder="Ex: Crèche Les Petits Loups, École maternelle Jean Moulin..."
                    style={inputStyle}
                    required
                    data-testid="input-employer"
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Années d'expérience dans le domaine *</label>
                  <select
                    name="experienceYears"
                    value={formData.experienceYears}
                    onChange={handleInputChange}
                    style={inputStyle}
                    required
                    data-testid="select-experienceYears"
                  >
                    <option value="">Sélectionnez une durée</option>
                    <option value="moins-1">Moins d'1 an</option>
                    <option value="1-3">1 à 3 ans</option>
                    <option value="3-5">3 à 5 ans</option>
                    <option value="5-10">5 à 10 ans</option>
                    <option value="plus-10">Plus de 10 ans</option>
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Description de vos tâches principales *</label>
                  <textarea
                    name="mainTasks"
                    value={formData.mainTasks}
                    onChange={handleInputChange}
                    placeholder="Décrivez vos principales responsabilités et activités quotidiennes..."
                    style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                    required
                    data-testid="textarea-mainTasks"
                  />
                  <p className="caption" style={{ marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                    Ces informations nous aideront à personnaliser votre Livret 1
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Projet VAE */}
            {currentStep === 3 && (
              <div>
                <h2 className="heading-2" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Target size={24} style={{ color: '#E75480' }} />
                  Votre projet VAE
                </h2>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Diplôme visé *</label>
                  <select
                    name="targetDiploma"
                    value={formData.targetDiploma}
                    onChange={handleInputChange}
                    style={inputStyle}
                    required
                    data-testid="select-targetDiploma"
                  >
                    <option value="">Sélectionnez un diplôme</option>
                    {VAE_PROGRAMS.map(program => (
                      <option key={program.id} value={program.id}>
                        {program.title} - {program.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Vos motivations pour la VAE *</label>
                  <textarea
                    name="motivations"
                    value={formData.motivations}
                    onChange={handleInputChange}
                    placeholder="Pourquoi souhaitez-vous obtenir ce diplôme ? Quels sont vos objectifs professionnels ?"
                    style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                    required
                    data-testid="textarea-motivations"
                  />
                </div>

                <div>
                  <label style={labelStyle}>Vos disponibilités pour les séances *</label>
                  <select
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                    style={inputStyle}
                    required
                    data-testid="select-availability"
                  >
                    <option value="">Sélectionnez vos disponibilités</option>
                    <option value="matin">En semaine - Matin (9h-12h)</option>
                    <option value="apres-midi">En semaine - Après-midi (14h-18h)</option>
                    <option value="soir">En semaine - Soir (18h-20h)</option>
                    <option value="samedi">Samedi</option>
                    <option value="flexible">Flexible - Je m'adapte</option>
                  </select>
                </div>
              </div>
            )}

            {/* Step 4: Informations complémentaires */}
            {currentStep === 4 && (
              <div>
                <h2 className="heading-2" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <FileText size={24} style={{ color: '#E75480' }} />
                  Informations complémentaires
                </h2>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem',
                    cursor: 'pointer',
                    padding: '1rem',
                    background: 'var(--bg-section)',
                    borderRadius: '0.5rem'
                  }}>
                    <input
                      type="checkbox"
                      name="hasCV"
                      checked={formData.hasCV}
                      onChange={handleInputChange}
                      style={{ width: '20px', height: '20px', accentColor: '#E75480' }}
                      data-testid="checkbox-hasCV"
                    />
                    <span className="body-medium">J'ai déjà un CV à jour que je pourrai fournir</span>
                  </label>
                </div>

                <div>
                  <label style={labelStyle}>Questions ou préoccupations particulières (optionnel)</label>
                  <textarea
                    name="questions"
                    value={formData.questions}
                    onChange={handleInputChange}
                    placeholder="Avez-vous des questions sur la VAE ? Y a-t-il des points particuliers que vous souhaitez aborder lors de notre premier rendez-vous ?"
                    style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                    data-testid="textarea-questions"
                  />
                </div>

                {/* Summary */}
                <div style={{ 
                  marginTop: '2rem',
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #FFE4EC 0%, #FFC0D0 100%)',
                  borderRadius: '0.75rem'
                }}>
                  <h3 className="heading-3" style={{ marginBottom: '1rem', color: '#E75480' }}>
                    Récapitulatif de votre profil
                  </h3>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <CheckCircle size={18} style={{ color: '#4CAF50', flexShrink: 0, marginTop: '0.1rem' }} />
                      <span className="body-small"><strong>Nom :</strong> {formData.firstName} {formData.lastName}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <CheckCircle size={18} style={{ color: '#4CAF50', flexShrink: 0, marginTop: '0.1rem' }} />
                      <span className="body-small"><strong>Poste :</strong> {formData.currentPosition}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <CheckCircle size={18} style={{ color: '#4CAF50', flexShrink: 0, marginTop: '0.1rem' }} />
                      <span className="body-small"><strong>Employeur :</strong> {formData.employer}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <CheckCircle size={18} style={{ color: '#4CAF50', flexShrink: 0, marginTop: '0.1rem' }} />
                      <span className="body-small"><strong>Diplôme visé :</strong> {VAE_PROGRAMS.find(p => p.id === formData.targetDiploma)?.title || '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div style={{ 
              display: 'flex', 
              justifyContent: currentStep === 1 ? 'flex-end' : 'space-between',
              marginTop: '2rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid var(--border-light)'
            }}>
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="btn-secondary"
                  style={{ gap: '0.5rem' }}
                  data-testid="btn-previous"
                >
                  <ArrowLeft size={18} />
                  Précédent
                </button>
              )}

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn-primary"
                  style={{ gap: '0.5rem' }}
                  data-testid="btn-next"
                >
                  Suivant
                  <ArrowRight size={18} />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                  style={{ 
                    gap: '0.5rem',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  data-testid="btn-submit"
                >
                  {isSubmitting ? 'Enregistrement...' : 'Valider mon profil'}
                  {!isSubmitting && <CheckCircle size={18} />}
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Info Box */}
        <div className="card accent-blue" style={{ marginTop: '2rem', padding: '1.5rem' }}>
          <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
            Pourquoi ces informations ?
          </h3>
          <p className="body-small" style={{ lineHeight: '1.7' }}>
            Ces informations nous permettent de préparer au mieux votre accompagnement et notamment 
            la rédaction de votre <strong>Livret 1 (dossier de recevabilité)</strong>, qui sera 
            personnalisé en fonction de votre parcours professionnel, votre lieu de travail et 
            le diplôme que vous souhaitez obtenir.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PreQualificationPage;
